import { css, StyleSheet } from 'aphrodite'
import * as React from 'react'

import { UnitAction } from '../../engine/actions/action'
import Game from '../../engine/game'
import Hex from '../../engine/hex'
import { ICell } from '../../engine/map'
import Unit from '../../engine/unit'
import Dialog from '../components/Dialog'
import Layout from '../components/layout'
import Screen from '../components/screen'
import MainStore from '../mainStore'
import style from '../utils/style'
import { HEX_POINTS, HEX_SIZE, projectHex } from './iso'
import Map from './map'
import Overlays from './overlays'
import Sidebar from './sidebar'
import Store from './store'
import Things from './things'

const TURN_BANNER_DURATION_MS = 500

const styles = StyleSheet.create({
  stage: {
    width: '100vw',
    height: '100vh',
    maxWidth: '100vw',
    maxHeight: '100vh',
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',

    '@media (max-width: 720px)': {
      flexDirection: 'column',
    },
  },
  mapContainer: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
  },
  mapPane: {
    position: 'relative',
    minWidth: 0,
    minHeight: 0,
    flex: '1 1 auto',
    overflow: 'hidden',
  },
  map: {
    display: 'block',
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    overflow: 'hidden',
  },
  zoomControls: {
    position: 'absolute',
    left: 12,
    bottom: 12,
    zIndex: 1,
    padding: '6px 8px',
    background: style.darkGrey,
    border: style.border,
  },
  zoomSlider: {
    display: 'block',
    width: 120,
  },
  turnBannerWrap: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    zIndex: 2,
  },
  turnBanner: {
    minWidth: 260,
    padding: '12px 28px',
    textAlign: 'center',
    background: 'rgba(29, 29, 32, .9)',
    border: '4px solid transparent',
    boxShadow: 'inset 0 0 16px rgba(255,255,255,.08)',
    fontFamily: 'VT323, monospace',
    fontSize: 44,
    lineHeight: 1,
    letterSpacing: 0,
    animationName: {
      '0%': {
        opacity: 0,
        transform: 'translateY(-12px) scale(.94)',
      },
      '15%': {
        opacity: 1,
        transform: 'translateY(0) scale(1)',
      },
      '75%': {
        opacity: 1,
        transform: 'translateY(0) scale(1)',
      },
      '100%': {
        opacity: 0,
        transform: 'translateY(-18px) scale(.98)',
      },
    },
    animationDuration: `${TURN_BANNER_DURATION_MS}ms`,
    animationTimingFunction: 'ease-out',
    animationFillMode: 'both',

    '@media (max-width: 720px)': {
      minWidth: 210,
      fontSize: 34,
    },
  },
  turnBannerPlayer: {
    color: '#00A000',
    borderColor: '#00A000',
    boxShadow: '0 0 22px #00A000, inset 0 0 16px rgba(255,255,255,.08)',
  },
  turnBannerEnemy: {
    color: '#A00000',
    borderColor: '#A00000',
    boxShadow: '0 0 22px #A00000, inset 0 0 16px rgba(255,255,255,.08)',
  },
  debugConsole: {
    position: 'absolute',
    left: 12,
    bottom: 56,
    width: 'min(620px, calc(100% - 24px))',
    maxHeight: '34vh',
    zIndex: 3,
    overflow: 'auto',
    padding: 10,
    textAlign: 'left',
    color: style.textColor,
    background: 'rgba(29, 29, 32, .94)',
    border: `3px solid ${style.gold}`,
    fontFamily: 'VT323, monospace',
    fontSize: 22,
    lineHeight: 1.15,
    boxSizing: 'border-box',
  },
  debugLine: {
    whiteSpace: 'pre-wrap',
  },
})

interface IViewBox {
  x: number
  y: number
  width: number
  height: number
}

interface IViewport {
  zoom: number
  panX: number
  panY: number
}

export interface IProps {
  store: MainStore,
}

export interface IState {
  playerFaction: string
  game: Game,
  themeId: string,
  viewport: IViewport,
  turnBanner?: {
    key: number,
    text: string,
    isPlayer: boolean,
  },
  showDebugConsole: boolean,
  debugLog: string[],

  selection?: {
    cell: ICell,
    unit?: {
      unit: Unit,
      paths: { [idx: string]: Hex },
      threats?: { [idx: string]: Hex },
      dangerPaths?: { [idx: string]: Hex },
      attackers?: { [idx: string]: Unit },
      attackersByPath?: { [idx: string]: { [idx: string]: Unit } },
      pendingMovementCompletion?: boolean,
      action?: {
        action: UnitAction,
        targets: { [idx: string]: Hex },
        selectedTarget?: Hex,
        area?: { [idx: string]: Hex },
      },
    },
  }

  hover?: {
    cell: ICell,
    unit?: {
      unit: Unit,
      paths: { [idx: string]: Hex },
      threats?: { [idx: string]: Hex },
      dangerPaths?: { [idx: string]: Hex },
      attackers?: { [idx: string]: Unit },
      attackersByPath?: { [idx: string]: { [idx: string]: Unit } },
    },
  }
}

export default class Stageview extends React.Component<IProps, IState> {
  oldKeyPress: any
  drag?: {
    x: number,
    y: number,
    panX: number,
    panY: number,
    viewWidth: number,
    viewHeight: number,
  }
  store: Store
  debugListeners: Array<() => void> = []

  constructor(props) {
    super(props)
    const currentGame = this.props.store.state.currentGame!

    this.store = new Store(this)
    this.state = {
      playerFaction: currentGame.playerFaction,
      game: currentGame.game,
      themeId: currentGame.theme.id,
      viewport: {
        zoom: 1,
        panX: 0,
        panY: 0,
      },
      showDebugConsole: false,
      debugLog: [],
    }
  }

  componentDidMount() {
    this.oldKeyPress = document.onkeypress
    document.onkeypress = this.onKeyPress
    document.addEventListener('mouseup', this.onDocumentMouseUp)
    this.debugListeners = [
      this.state.game.listen('unit:move', this.store.onDebugMove),
      this.state.game.listen('action:resolve', this.store.onDebugAction),
    ]
  }

  clampViewport(viewport: IViewport, bounds = this.getBaseMapBounds()) {
    const zoom = Math.max(1, Math.min(3, viewport.zoom))
    const viewWidth = bounds.width / zoom
    const viewHeight = bounds.height / zoom
    const maxPanX = (bounds.width - viewWidth) / 2
    const maxPanY = (bounds.height - viewHeight) / 2

    return {
      zoom,
      panX: Math.max(-maxPanX, Math.min(maxPanX, viewport.panX)),
      panY: Math.max(-maxPanY, Math.min(maxPanY, viewport.panY)),
    }
  }

  getBaseMapBounds(): IViewBox {
    const xs: number[] = []
    const ys: number[] = []
    const wallHeight = HEX_SIZE * 2
    const padding = HEX_SIZE * 3
    const corners = [
      HEX_POINTS.e,
      HEX_POINTS.se,
      HEX_POINTS.sw,
      HEX_POINTS.w,
      HEX_POINTS.nw,
      HEX_POINTS.ne,
    ]

    this.state.game.map.cells.forEach(cell => {
      const pos = projectHex(cell.pos)
      corners.forEach(corner => {
        xs.push(pos.x + corner.x)
        ys.push(pos.y + corner.y)
        ys.push(pos.y + corner.y + wallHeight)
      })
    })

    const minX = Math.min(...xs) - padding
    const minY = Math.min(...ys) - padding
    const maxX = Math.max(...xs) + padding
    const maxY = Math.max(...ys) + padding

    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
  }

  getMapViewBox() {
    const bounds = this.getBaseMapBounds()
    const viewport = this.clampViewport(this.state.viewport, bounds)
    const viewWidth = bounds.width / viewport.zoom
    const viewHeight = bounds.height / viewport.zoom
    const x = bounds.x + (bounds.width - viewWidth) / 2 + viewport.panX
    const y = bounds.y + (bounds.height - viewHeight) / 2 + viewport.panY

    return `${x} ${y} ${viewWidth} ${viewHeight}`
  }

  setZoom(zoom: number) {
    this.setState({
      viewport: this.clampViewport({
        zoom,
        panX: this.state.viewport.panX,
        panY: this.state.viewport.panY,
      }),
    })
  }

  onWheel = (e: any) => {
    e.preventDefault()
    const step = e.deltaY < 0 ? 1.12 : 1 / 1.12
    this.setZoom(this.state.viewport.zoom * step)
  }

  onZoomSliderChange = (e: any) => {
    this.setZoom(parseFloat(e.currentTarget.value))
  }

  onMapMouseDown = (e: any) => {
    if (e.button !== 2) {
      return
    }
    e.preventDefault()

    const bounds = this.getBaseMapBounds()
    const viewport = this.clampViewport(this.state.viewport, bounds)

    this.drag = {
      x: e.clientX,
      y: e.clientY,
      panX: viewport.panX,
      panY: viewport.panY,
      viewWidth: bounds.width / viewport.zoom,
      viewHeight: bounds.height / viewport.zoom,
    }
  }

  onMapMouseMove = (e: any) => {
    if (!this.drag) {
      return
    }

    const svg = this.refs.svg as any
    const rect = svg.getBoundingClientRect()
    const dx = e.clientX - this.drag.x
    const dy = e.clientY - this.drag.y

    this.setState({
      viewport: this.clampViewport({
        zoom: this.state.viewport.zoom,
        panX: this.drag.panX - dx * this.drag.viewWidth / rect.width,
        panY: this.drag.panY - dy * this.drag.viewHeight / rect.height,
      }),
    })
  }

  onDocumentMouseUp = () => {
    this.drag = undefined
  }

  onKeyPress = (e: KeyboardEvent) => {
    if (e.key === '`' || e.key === '~') {
      const showDebugConsole = !this.state.showDebugConsole
      this.setState({
        showDebugConsole,
        debugLog: showDebugConsole
          ? this.store.debugLog
          : this.state.debugLog,
      })
      e.preventDefault()
      return
    }

    if (this.state.turnBanner) {
      return
    }

    const { game, playerFaction, selection } = this.state
    const unit = selection && selection.unit && selection.unit.unit
    const action = selection && selection.unit && selection.unit.action
    const int = parseInt(e.key, 10) - 1

    if (e.key === ' ' && !action) {
      const isAval = u => u.canPerformAction || u.mp > 0

      const playerUnits = game.factionUnits[playerFaction]
      const currentUnitIndex = playerUnits.findIndex(u =>
        u.id === (unit && unit.id),
      )
      const nextAvailableUnit = playerUnits.find(
        (u, i) => isAval(u) && i > currentUnitIndex,
      ) || playerUnits.find(isAval)
      if (nextAvailableUnit) {
        this.store.selectCell(game.map.cellAt(nextAvailableUnit.pos))
        e.preventDefault()
      }
    } else if (
      unit && unit.factionId === playerFaction && unit.actions[int]
      && unit.canPerformAction
    ) {
      this.store.selectAction(unit.actions[int])
    }
  }

  componentWillUnmount() {
    document.onkeypress = this.oldKeyPress
    document.removeEventListener('mouseup', this.onDocumentMouseUp)
    this.debugListeners.forEach(unsubscribe => unsubscribe())
    if (this.store.turnBannerTimeout !== undefined) {
      window.clearTimeout(this.store.turnBannerTimeout)
    }
  }

  renderGameOver(winningFaction: string) {
    const { finishGame } = this.props.store
    const { playerFaction } = this.state
    const { level, reward } = this.props.store.state.currentGame!
    const playerWon = winningFaction === playerFaction
    const goldGained = playerWon ? reward : 0

    return (
      <Dialog compact>
        <Dialog.Title>
          {playerWon ? `Round ${level + 1} Cleared!` : 'GAME OVER'}
        </Dialog.Title>
        <Dialog.Content>
          <div>{playerWon ? 'YOU WON' : 'YOU LOST'}</div>
          <div>Gold gained: {goldGained}</div>
        </Dialog.Content>
        <Dialog.Controls>
          <Dialog.Control onClick={() => finishGame(playerWon)}>
            OK
          </Dialog.Control>
        </Dialog.Controls>
      </Dialog>
    )
  }

  renderTurnBanner() {
    const { turnBanner } = this.state
    if (!turnBanner) {
      return null
    }

    return (
      <div className={css(styles.turnBannerWrap)}>
        <div
          className={css(
            styles.turnBanner,
            turnBanner.isPlayer
              ? styles.turnBannerPlayer
              : styles.turnBannerEnemy,
          )}
          key={turnBanner.key}
        >
          {turnBanner.text}
        </div>
      </div>
    )
  }

  renderDebugConsole() {
    if (!this.state.showDebugConsole) {
      return null
    }

    const lines = this.state.debugLog.length
      ? this.state.debugLog
      : ['Debug console enabled']

    return (
      <div className={css(styles.debugConsole)}>
        {lines.map((line, idx) => (
          <div className={css(styles.debugLine)} key={idx}>
            {line}
          </div>
        ))}
      </div>
    )
  }

  render() {
    const winningFaction = this.state.game.checkGameOver()
    let dialog
    if (winningFaction) {
      dialog = this.renderGameOver(winningFaction)
    }
    const viewBox = this.getMapViewBox()

    return (
      <Screen direction="row" classes={[styles.stage]}>
        {dialog}
        <Layout justify="center" grow classes={[styles.mapPane]}>
          <div className={css(styles.mapContainer)}>
            <div className={css(styles.zoomControls)}>
              <input
                className={css(styles.zoomSlider)}
                type="range"
                min="1"
                max="3"
                step="0.05"
                value={this.state.viewport.zoom}
                onChange={this.onZoomSliderChange}
              />
            </div>
            <svg
              ref="svg"
              viewBox={viewBox}
              className={css(styles.map)}
              preserveAspectRatio="xMidYMid meet"
              onWheel={this.onWheel}
              onMouseDown={this.onMapMouseDown}
              onMouseMove={this.onMapMouseMove}
              onContextMenu={e => e.preventDefault()}
              onMouseLeave={() => this.store.hover(null)}
            >
              <g ref="map">
                <Map store={this.store} />
              </g>
              <g style={{ pointerEvents: 'none' }}>
                <Overlays store={this.store} />
                <Things store={this.store} />
              </g>
            </svg>
            {this.renderTurnBanner()}
            {this.renderDebugConsole()}
          </div>
        </Layout>
        <Sidebar store={this.store} />
      </Screen>
    )
  }
}



// WEBPACK FOOTER //
// ./src/ui/stageView/index.tsx